/**
 * Resolve the pipeline-view deep link for a stage entity.
 *
 * Returns the route to the pipeline the stage belongs to, with `?stage=<id>`
 * appended — PipelineBoard reads that search param and scrolls the matching
 * column into view (`initialScrollToStageId`). This is the same URL shape the
 * side navigation builds (`buildPipelineNavItem`) and the pipeline stage
 * pill links to:
 *
 *   /<orgId>/pipeline/<entitySlug>/<pipelineId>?stage=<stageEntityId>
 *
 * Returns null when the link can't or shouldn't be built:
 * - `enabled` is false — the caller has no use for a route (e.g. a
 *   non-interactive or empty-value badge); no fetch work is done either
 * - the stage isn't in any of the org's pipelines (still loading, deleted,
 *   or a legacy non-UUID stage name)
 * - the pipeline's base entity type can't be resolved, or resolves to a
 *   `*_timeline` type (not a real entity type — no matching route)
 */

import { useMemo } from 'react';

import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { entityTypeToRouteSlug } from '@/lib/crm/utils/entityTypeUtils';

export interface UseStagePipelineRouteOptions {
  /**
   * When false, the hook does no fetch work and always resolves to `null` —
   * for badges that can't or shouldn't navigate (non-interactive, no value).
   * `usePipelines` receives `undefined` (skips its query via its own
   * `enabled: !!orgId`).
   */
  enabled?: boolean;
}

export function useStagePipelineRoute(
  orgId: number | undefined,
  stageEntityId: string | null | undefined,
  { enabled = true }: UseStagePipelineRouteOptions = {}
): string | null {
  // Unfiltered fetch — cached for 5 min per org and seeded from localStorage
  // (see usePipelines), so badge-heavy surfaces share one request.
  const { pipelines } = usePipelines(enabled ? orgId : undefined);

  return useMemo(() => {
    if (!enabled || !orgId || !stageEntityId) {
      return null;
    }
    for (const pipeline of pipelines) {
      if (!pipeline.entity_id) {
        continue;
      }
      if (!pipeline.stages.some(s => s.entity_id === stageEntityId)) {
        continue;
      }
      // Prefer the explicit base type; fall back to deriving it from the
      // pipeline type name (e.g. 'ahoy_deal_pipeline' → 'ahoy_deal') for
      // older payloads where base_entity_type_name is empty.
      const base =
        pipeline.base_entity_type_name ||
        (pipeline.entity_type_name.endsWith('_pipeline')
          ? pipeline.entity_type_name.slice(0, -'_pipeline'.length)
          : '');
      if (!base) {
        return null;
      }
      // A *_timeline base_entity_type_name (or a pipeline type name that
      // derives one) isn't a real entity type — it would build a broken
      // deep link (no matching route/entity type).
      if (base.endsWith('_timeline')) {
        return null;
      }
      return `/${orgId}/pipeline/${entityTypeToRouteSlug(base)}/${pipeline.entity_id}?stage=${stageEntityId}`;
    }
    return null;
  }, [enabled, orgId, stageEntityId, pipelines]);
}
